import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

// Piezas de UI compartidas entre ViajeTrasladoDetailPage.tsx y los
// subcomponentes de cada paso (ej. ViajeTrasladoMedioDePagoDetail.tsx),
// para que cada paso pueda mostrarse como varias cards temáticas en vez de
// una sola card gigante (mismo criterio que EventoDetailPage.tsx).

export const sectionCardClass =
  'rounded-2xl border border-white bg-whiteLight p-6 dark:border-white/10 dark:bg-darkLight';

export function SectionCardHeader({
  icon: Icon,
  title
}: {
  icon: LucideIcon;
  title: string;
}) {
  return (
    <div className="mb-6 flex items-center gap-2">
      <Icon size={18} className="text-secondary-500" />
      <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
        {title}
      </h2>
    </div>
  );
}

// Mapa explícito de colores a clases completas: Tailwind no puede generar
// clases compuestas a partir de un template literal con un valor recibido
// en runtime (mismo criterio que ProfileResumeBadge).
const pillColorClasses = {
  gray: 'bg-secondary-50 text-inkMuted dark:bg-white/10 dark:text-inkMuted-dark',
  blue: 'bg-secondary-100 text-secondary-700 dark:bg-white/10 dark:text-secondary-200',
  green:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300'
} as const;

const pillSizeClasses = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm font-semibold'
} as const;

export function Pill({
  color = 'gray',
  size = 'sm',
  children
}: {
  color?: keyof typeof pillColorClasses;
  size?: keyof typeof pillSizeClasses;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${pillColorClasses[color]} ${pillSizeClasses[size]}`}
    >
      {children}
    </span>
  );
}

export const fieldLabelClass =
  'text-xs font-medium uppercase tracking-wide text-inkMuted dark:text-inkMuted-dark';

export function FieldItem({
  label,
  value
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div>
      <p className={fieldLabelClass}>{label}</p>
      <p className="text-sm font-medium text-ink dark:text-ink-dark">
        {value}
      </p>
    </div>
  );
}

export function ObservationItem({
  label,
  text
}: {
  label: string;
  text: ReactNode;
}) {
  return (
    <div>
      <p className={fieldLabelClass}>{label}</p>
      <p className="whitespace-pre-line break-words text-sm leading-relaxed text-ink dark:text-ink-dark">
        {text}
      </p>
    </div>
  );
}
